import { isBoolean, isNumber, isString } from 'lodash';
import posthog from 'posthog-js';
import React from 'react';
import { useDeepCompareMemoize } from 'use-deep-compare-effect';

import { FeatureFlags, SearchAddress, StateResponse } from 'api/api';

import { globalConfig } from '~src/context/config';
import { GlobalStateContext } from '~src/global-state';

export const useInitPostHog = () => {
  // TODO: Use an observer to see if we're ready instead and lift usage up
  const globalState = React.useContext(GlobalStateContext);
  const propertiesUpdated = useDeepCompareMemoize(getBaseEventProperties(globalState));
  const [postHogAvailable, setPosthogAvailable] = React.useState(false);

  const memoizedProperties = React.useMemo(() => {
    const props = getBaseEventProperties(globalState);
    // Register is sent when posthog loads so we want to skip the first emit
    if (postHogAvailable) {
      posthog.register(props);
    }
    return props;

    // Only register new properties if they've changed
  }, [!!globalState, propertiesUpdated]);

  React.useEffect(() => {
    if (!globalState) {
      return;
    }

    const { flags } = globalState.sessions[0];

    if (flags.enable_analytics) {
      posthog.init(globalConfig.posthogKey, {
        api_host: 'https://eu.posthog.com',
        autocapture: false,
        disable_session_recording: true,
        ip: false,
        loaded: ph => {
          ph.register(memoizedProperties);
          ph.identify(globalState.checkout_session_id);
          setPosthogAvailable(true);
        },
      });
    }
  }, [!!globalState]);
};

const getBaseEventProperties = (globalState: StateResponse | undefined) => {
  if (!globalState || !globalState.sessions[0].flags.enable_analytics) {
    return {};
  }
  const { flags, experiment_id, cart, variant_site_id } = globalState.sessions[0];
  const truthyFlags = Object.entries(flags).filter(([_, value]) => !!value);

  const enabledBooleanFlags = truthyFlags
    .filter(([_, value]) => isBoolean(value))
    .reduce<string[]>((initial, [name]) => [...initial, name], []);

  const stringOrNumberFlags: NonBooleanFlags = truthyFlags
    .filter(([_, value]) => isString(value) || isNumber(value))
    .reduce(
      (initial, [name, value]) => ({ ...initial, [`flag-${name}`]: value }),
      {} as NonBooleanFlags
    );

  return {
    experiment_ids: experiment_id ? [experiment_id] : [],
    journey_id: globalState.checkout_session_id,
    source: 'checkout_widget',
    checkout_session_id: globalState.checkout_session_id,
    purchase_country: globalState.purchase_country,
    locale: globalState.locale,
    currency: globalState.purchase_currency,
    checkout_version: process.env.BUILD_REV || process.env.ENV,
    cart_value: cart.total_value,
    site_id: globalState.site_id,
    variant_site_id: variant_site_id,
    checkout_enabled_boolean_flags: enabledBooleanFlags,
    ...stringOrNumberFlags,
  };
};

// This can be implemented a bit more elegantly using https://artsy.github.io/blog/2018/11/21/conditional-types-in-typescript/
// but that's good enough for now
export const logPosthogEvent = <EventKey extends keyof PosthogEvents>(
  name: EventKey,
  ...properties: PosthogEvents[EventKey]
) => {
  posthog.capture(name, ...properties);
};

export const useLogPosthogEvent = () => {
  const globalState = React.useContext(GlobalStateContext);
  const { flags } = globalState!.sessions[0];

  return flags.enable_analytics ? logPosthogEvent : () => null;
};

interface NonBooleanFlags {
  'flag-shipping_categories_visible_when_folded_count': FeatureFlags['shipping_categories_visible_when_folded_count'];
  'flag-upsell_color': FeatureFlags['upsell_color'];
}

interface LocationDetails {
  name: string;
  price: number;
  type: string | undefined;
  distance_in_meters: number | undefined;
}

type PosthogEvents = {
  'Category Changed': [
    {
      shipping_category: string;
      shipping_method: string;
      time_slot_ref: string;
      location_ref?: string;
    },
  ];
  'Categories Folded': [{ categories_hidden: number }];
  'Categories Unfolded': [{ categories_shown: number }];
  'Address Required For Category Modal Viewed': [];
  'Search Address Changed': [SearchAddress];
  'Inline Postal Code Changed': [{ postal_code: string }];
  'Pickup Location Modal Viewed': [];
  'Pickup Location Pin Clicked': [LocationDetails];
  'Pickup Location List Item Clicked': [LocationDetails];
  'Pickup Location Changed': [LocationDetails];
  'Pickup Location Modal Canceled': [];
  'No Shipping Options Error Viewed': [];
  'Invalid Postal Code Error Viewed': [];
  'General Address Change Error Viewed': [];
  'Provide New Address Button Clicked': [];
  'Address Change Canceled': [];
  'Address Required For Pickup Location Modal Viewed': [];
  'Address Label Clicked': [];
};
